import { z } from 'zod';

/** UUID do Postgres — formato relaxado (não exige variant bits RFC4122). */
const pgUuid = z.string().regex(
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  'UUID inválido',
);

/** Schema de linha rh_employees — valida respostas da API antes de mapear para Employee. */
export const rhEmployeeRowSchema = z.object({
  id: pgUuid,
  company_id: pgUuid,
  column_id: pgUuid,
  employee_code: z.string().nullable().optional(),
  name: z.string().min(1),
  role: z.string(),
  status: z.string(),
  probation_period: z.string().nullable().optional(),
  birth_date: z.string().nullable().optional(),
  hiring_date: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  last_birthday_msg_year: z.number().nullable().optional(),
  avatar: z.string().nullable().optional(),
  sort_order: z.number().optional(),
  cost_center_code: z.string().nullable().optional(),
  cost_center_description: z.string().nullable().optional(),
});

/** Campos opcionais de auditoria nas ocorrências (JOIN com rh_users na API). */
const rhRecordAuditFields = {
  created_by: pgUuid.nullable().optional(),
  created_at: z.string().nullable().optional(),
  created_by_username: z.string().nullable().optional(),
};

/** Schema de linha rh_absences. */
export const rhAbsenceRowSchema = z.object({
  id: pgUuid,
  employee_id: pgUuid,
  date: z.string(),
  reason: z.string(),
  ...rhRecordAuditFields,
});

/** Schema de linha rh_situations. */
export const rhSituationRowSchema = z.object({
  id: pgUuid,
  employee_id: pgUuid,
  date: z.string(),
  description: z.string(),
  ...rhRecordAuditFields,
});

/** Schema de linha rh_delays. */
export const rhDelayRowSchema = z.object({
  id: pgUuid,
  employee_id: pgUuid,
  date: z.string(),
  reason: z.string(),
  ...rhRecordAuditFields,
});

/** Schema de linha rh_early_departures. */
export const rhEarlyDepartureRowSchema = z.object({
  id: pgUuid,
  employee_id: pgUuid,
  date: z.string(),
  reason: z.string(),
  ...rhRecordAuditFields,
});

/** Schema de linha rh_users (sem password_hash — nunca exposto ao client). */
export const rhUserRowSchema = z.object({
  id: pgUuid,
  username: z.string().min(1),
  perm_dashboard: z.boolean(),
  perm_hr: z.boolean(),
  perm_settings: z.boolean(),
  perm_absences: z.boolean(),
  perm_employee_create: z.boolean().optional(),
  perm_employee_edit: z.boolean().optional(),
  perm_employee_delete: z.boolean().optional(),
  perm_settings_detail: z.unknown().optional(),
});

/** Schema de linha rh_columns. */
export const rhColumnRowSchema = z.object({
  id: pgUuid,
  company_id: pgUuid,
  column_key: z.string().min(1),
  title: z.string(),
  color: z.string(),
  hiring_limit: z.number(),
  sort_order: z.number(),
});

/** Centro de custo em rh_settings.value. */
export const rhCostCenterSchema = z.object({
  code: z.string(),
  description: z.string(),
});

/** Valores JSON de rh_settings validados por chave. */
export const rhRolesSettingSchema = z.array(z.string());
export const rhRoleColorsSettingSchema = z.record(z.string(), z.string());
export const rhAfastamentoDatesSettingSchema = z.record(z.string(), z.string());
export const rhCostCentersSettingSchema = z.array(rhCostCenterSchema);

export type RhEmployeeRow = z.infer<typeof rhEmployeeRowSchema>;
export type RhAbsenceRow = z.infer<typeof rhAbsenceRowSchema>;
export type RhSituationRow = z.infer<typeof rhSituationRowSchema>;
export type RhDelayRow = z.infer<typeof rhDelayRowSchema>;
export type RhEarlyDepartureRow = z.infer<typeof rhEarlyDepartureRowSchema>;
export type RhUserRow = z.infer<typeof rhUserRowSchema>;
export type RhColumnRow = z.infer<typeof rhColumnRowSchema>;
export type RhCostCenterRow = z.infer<typeof rhCostCenterSchema>;

/**
 * Valida array de linhas do Postgres; descarta inválidas e loga aviso.
 * @param schema - Schema Zod do tipo de linha
 * @param rows - Dados brutos da API
 * @param label - Nome da tabela para log de diagnóstico
 * @returns Linhas válidas tipadas
 */
export function parseDbRows<T>(
  schema: z.ZodType<T>,
  rows: unknown[],
  label: string,
): T[] {
  const valid: T[] = [];
  for (const row of rows) {
    const parsed = schema.safeParse(row);
    if (parsed.success) {
      valid.push(parsed.data);
    } else {
      console.warn(`[rhDataService] Linha inválida em ${label}:`, parsed.error.flatten().fieldErrors);
    }
  }
  return valid;
}

/**
 * Valida uma única linha; lança se inválida (mutações com RETURNING).
 * @param schema - Schema Zod
 * @param row - Linha bruta
 * @param label - Nome da tabela
 * @returns Linha tipada
 */
export function parseDbRow<T>(schema: z.ZodType<T>, row: unknown, label: string): T {
  const parsed = schema.safeParse(row);
  if (!parsed.success) {
    throw new Error(`Resposta inválida de ${label}: ${JSON.stringify(parsed.error.flatten().fieldErrors)}`);
  }
  return parsed.data;
}

/**
 * Valida valor JSON de rh_settings; retorna null se inválido (com log de diagnóstico).
 * @param schema - Schema Zod do valor esperado
 * @param value - JSON bruto da coluna value
 * @param key - Chave do setting para log
 * @returns Valor tipado ou null
 */
export function parseSettingValue<T>(
  schema: z.ZodType<T>,
  value: unknown,
  key: string,
): T | null {
  if (value == null) return null;
  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    console.warn(`[rhDataService] Setting inválido (${key}):`, parsed.error.flatten().fieldErrors);
    return null;
  }
  return parsed.data;
}
